import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/utils';
import { Button } from './button';
import { Input } from './form';
import { Modal, Field } from './system';
interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  tone?: 'danger' | 'primary';
  requireText?: string;
  action: () => Promise<unknown> | unknown;
  successMessage?: string;
}
const Context = createContext<(options: ConfirmOptions) => void>(() => undefined);
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const request = useCallback((next: ConfirmOptions) => {
    setTyped('');
    setOptions(next);
  }, []);
  const run = async () => {
    if (!options || lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      await options.action();
      if (options.successMessage) toast.success(options.successMessage);
      setOptions(null);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
      lock.current = false;
    }
  };
  const cancel = () => {
    if (busy) return;
    options?.onCancel?.();
    setOptions(null);
  };
  return (
    <Context.Provider value={request}>
      {children}
      <Modal
        open={!!options}
        onClose={cancel}
        title={options?.title || 'Confirm action'}
        description={typeof options?.description === 'string' ? options.description : undefined}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={cancel}
              disabledReason="Please wait while the current action finishes."
            >
              {options?.cancelLabel || 'Cancel'}
            </Button>
            <Button
              variant={options?.tone === 'primary' ? 'primary' : 'danger'}
              loading={busy}
              disabled={!!options?.requireText && typed.trim() !== options.requireText}
              disabledReason={`Type ${options?.requireText} to confirm this action.`}
              onClick={run}
            >
              {options?.confirmLabel || 'Confirm'}
            </Button>
          </>
        }
      >
        {options?.description && typeof options.description !== 'string' && (
          <div className="mb-4 text-xs text-fg-muted">{options.description}</div>
        )}
        {options?.requireText && (
          <Field label={`Type ${options.requireText} to confirm`}>
            <Input
              aria-label="Confirmation text"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </Field>
        )}
      </Modal>
    </Context.Provider>
  );
}
export const useConfirm = () => useContext(Context);
